// Con dos o más celdas seleccionadas (área o shift+click): agregar todas al chat,
// ordenarlas en una grilla (ADR-2026-09-17-la-seleccion-se-ordena-en-grilla) o bajarlas
// juntas en UN zip (ADR-2026-09-16-la-seleccion-del-lienzo-se-baja-en-zip), con la
// pregunta de marca de agua hecha UNA vez. El zip lo arma `descargarComoZip`, el
// mismo de "descargar todo" del chat; si alguna falla se saltea y se avisa cuántas.

import { useContext, useState } from 'react';
import { Panel } from '@xyflow/react';
import { Download, LayoutGrid, Loader2, MessageSquarePlus } from 'lucide-react';
import { OPCIONES_SIN_TOCAR } from '../../components/descarga/useOpcionesDeDescarga';
import { descargarComoZip, type ArchivoDeLote } from '../../utils/descargarLote';
import { buildDownloadFileName } from '../../utils/downloadFileName';
import { firmarEnLote } from '../../utils/firmarEnLote';
import { adjuntoDeCelda } from './accionesDeCelda';
import { BOTON_DE_BARRA } from './BarraDeCelda';
import { LienzoContext } from './lienzoContext';

/** Nombre del zip: de dónde es y de cuándo. */
const nombreDelZip = (ahora: Date = new Date()): string =>
  `atlas-agente-${ahora.toISOString().slice(0, 10)}.zip`;

export const BarraDeSeleccion: React.FC = () => {
  const lienzo = useContext(LienzoContext);
  const [progreso, setProgreso] = useState<{ hechos: number; total: number } | null>(null);
  const celdas = lienzo.seleccionadas;
  if (celdas.length < 2) return null;

  const agregarTodas = () => {
    for (const c of celdas) {
      const chip = adjuntoDeCelda(c);
      if (chip) lienzo.agregarAdjunto(chip);
    }
  };
  const descargarTodas = async () => {
    if (progreso) return;
    try {
      const hayImagen = celdas.some((c) => !c.esVideo);
      const eleccion = hayImagen
        ? await lienzo.resolverOpciones({ cantidad: celdas.length })
        : OPCIONES_SIN_TOCAR;
      if (eleccion === null) return;
      setProgreso({ hechos: 0, total: celdas.length });
      // Los originales, firmados en una sola ida (casi todos ya están en la caché).
      const paths = celdas.map((c) => `${c.companyId}/full/${c.datos.fileName ?? ''}`);
      const firmas = await firmarEnLote(paths);
      const archivos: ArchivoDeLote[] = [];
      celdas.forEach((c, i) => {
        const url = c.datos.fileName ? firmas.get(paths[i]) : undefined;
        if (!url) return;
        archivos.push({
          url,
          nombre: buildDownloadFileName({
            variantName: c.datos.varianteNombre ?? undefined,
            originalFileName: c.datos.fileName ?? undefined,
            forceVideo: c.esVideo,
          }),
          datosGeneracion: c.datos.datosGeneracion,
        });
      });
      // La marca de agua sólo se compone sobre imágenes: un video pasa tal cual.
      const transformar = (blob: Blob, archivo: ArchivoDeLote) =>
        lienzo.aplicarOpciones(
          blob,
          blob.type.startsWith('video/'),
          blob.type.startsWith('video/') ? OPCIONES_SIN_TOCAR : eleccion,
          archivo.datosGeneracion,
        );
      const r = await descargarComoZip(
        archivos,
        nombreDelZip(),
        (hechos, total) => setProgreso({ hechos, total }),
        transformar,
      );
      const sinArchivo = celdas.length - archivos.length;
      if (r.ok === 0) lienzo.avisarError('No se pudo descargar ninguna. Probá de nuevo.');
      else if (r.fallidos + sinArchivo > 0) {
        lienzo.avisarError(`Bajé ${r.ok}; ${r.fallidos + sinArchivo} no se pudieron.`);
      }
    } catch (e) {
      lienzo.avisarError(e instanceof Error ? e.message : 'No se pudo descargar.');
    } finally {
      setProgreso(null);
    }
  };

  return (
    // Encima de la barra de herramientas, que ocupa el pie (misma altura que la paleta
    // del lápiz en Flujos).
    <Panel position="bottom-center" className="!m-0 !mb-[68px]">
      <div className="flex items-center gap-1 rounded-full border border-[#02062B]/[0.06] bg-white p-1 pl-4 shadow-[0_6px_20px_rgba(2,6,43,0.12)]">
        <span className="text-[12.5px] font-medium text-[#02062B]/[0.55]">
          {celdas.length} seleccionadas
        </span>
        <button type="button" onClick={agregarTodas} className={BOTON_DE_BARRA}>
          <MessageSquarePlus className="h-4 w-4" aria-hidden="true" />
          {lienzo.rotuloAgregar ?? 'Agregar al chat'}
        </button>
        <button type="button" onClick={lienzo.ordenarSeleccion} className={BOTON_DE_BARRA} title="Acomodarlas en una grilla">
          <LayoutGrid className="h-4 w-4" aria-hidden="true" />
          Ordenar
        </button>
        <button
          type="button"
          onClick={descargarTodas}
          disabled={!!progreso}
          className={BOTON_DE_BARRA}
          title="Descargar todas en un zip"
        >
          {progreso ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Download className="h-4 w-4" aria-hidden="true" />
          )}
          {progreso ? `${progreso.hechos}/${progreso.total}` : 'Descargar'}
        </button>
      </div>
    </Panel>
  );
};
